// src/pages/ClientList.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Snackbar, Alert, TextField, Checkbox,
  Pagination, Stack, CircularProgress, Tooltip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { getAllClients, deleteClient } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import FormClient from './FormClient';
import SortableTh from './common/SortableTh';
import { StatusAtivoChip, sxLinhaInativa } from './common/StatusAtivo';
import { useServerSort } from '../hooks/useServerSort';

// helpers de formatação
const onlyDigits = (v) => String(v || '').replace(/\D/g, '');

const fmtCPF = (v) => {
  const d = onlyDigits(v).slice(0, 11);
  if (!d) return '';
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};

const fmtCNPJ = (v) => {
  const d = onlyDigits(v).slice(0, 14);
  if (!d) return '';
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

// escolhe CPF ou CNPJ pela quantidade de dígitos
const fmtDoc = (v) => {
  const d = onlyDigits(v);
  if (!d) return '-';
  return d.length <= 11 ? fmtCPF(d) : fmtCNPJ(d);
};

function ClientList() {
  // paginação server-side
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina] = useState(20);

  // dados
  const [clientes, setClientes] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);

  // busca
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [ativoFiltro, setAtivoFiltro] = useState('');

  // UI
  const [dialogoAberto, setDialogoAberto] = useState(false);
  const [idsParaExcluir, setIdsParaExcluir] = useState([]);
  const [mensagemSnackbar, setMensagemSnackbar] = useState('');
  const [snackbarAberto, setSnackbarAberto] = useState(false);
  const [severidadeAlerta, setSeveridadeAlerta] = useState('success');
  const [selecionados, setSelecionados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [form, setForm] = useState({ open: false, id: null });

  const { orderBy, order, requestSort: aplicarOrdem } = useServerSort('razaoSocial', 'asc');
  const requestSort = (key) => {
    setPagina(1);
    aplicarOrdem(key);
  };

  const carregarPagina = useCallback(async () => {
    try {
      setCarregando(true);
      const resp = await getAllClients({
        indice: pagina,
        qtde: itensPorPagina,
        termo: searchTerm.trim(),
        ativo: ativoFiltro,
        ordenar: orderBy,
        direcao: order,
      });
      const data = resp.data;
      setClientes(data.items || []);
      setTotalPaginas(data.totalPages || 1);
      setTotalItens(data.totalItems || 0);
      setSelecionados([]);
    } catch {
      setMensagemSnackbar('Erro ao carregar clientes');
      setSeveridadeAlerta('error');
      setSnackbarAberto(true);
    } finally {
      setCarregando(false);
    }
  }, [pagina, itensPorPagina, searchTerm, ativoFiltro, orderBy, order]);

  useEffect(() => {
    carregarPagina();
  }, [carregarPagina]);

  // busca
  const onKeyDownBusca = (e) => e.key === 'Enter' && (setPagina(1), setSearchTerm(searchText));
  const acionarBusca = () => { setPagina(1); setSearchTerm(searchText); };

  // seleção
  const clientesPagina = clientes;
  const clientesOrdenados = clientesPagina;
  const selecionarTodos = (e) => {
    setSelecionados(e.target.checked ? clientesPagina.map(c => c.id) : []);
  };
  const selecionar = (id) => {
    setSelecionados(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // exclusão
  const abrirDialogo = (ids) => {
    setIdsParaExcluir(Array.isArray(ids) ? ids : [ids]);
    setDialogoAberto(true);
  };
  const fecharDialogo = () => { setDialogoAberto(false); setIdsParaExcluir([]); };
  const excluir = async () => {
    if (idsParaExcluir.length === 0) return;
    try {
      await Promise.all(idsParaExcluir.map(id => deleteClient(id)));
      setMensagemSnackbar(
        idsParaExcluir.length > 1
          ? `${idsParaExcluir.length} clientes excluídos com sucesso!`
          : 'Cliente excluído com sucesso!'
      );
      setSeveridadeAlerta('success');
      await carregarPagina();
      if (clientesPagina.length === idsParaExcluir.length && pagina > 1) setPagina(pagina - 1);
    } catch (err) {
      setMensagemSnackbar(getApiErrorMessage(err, 'Não foi possível excluir o(s) cliente(s).'));
      setSeveridadeAlerta('error');
    } finally {
      setSnackbarAberto(true);
      fecharDialogo();
    }
  };

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setMensagemSnackbar(msg);
    setSeveridadeAlerta('success');
    setSnackbarAberto(true);
    carregarPagina();
  };

  const mudarPagina = (_e, p) => setPagina(p);
  const fecharSnackbar = () => setSnackbarAberto(false);

  // header checkbox
  const todosSelecionados = useMemo(
    () => clientesPagina.length > 0 && selecionados.length === clientesPagina.length,
    [clientesPagina, selecionados]
  );
  const indeterminate = selecionados.length > 0 && !todosSelecionados;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Clientes"
        subtitle="Cadastro de clientes usado no PDV e no convênio."
        actions={
          <>
            {selecionados.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => abrirDialogo(selecionados)}
              >
                Excluir selecionados ({selecionados.length})
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirForm()}>
              Novo cliente
            </Button>
          </>
        }
      />

      {/* Filtros / Busca */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Buscar por nome, fantasia, CPF/CNPJ ou telefone..."
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={onKeyDownBusca}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchText && (
              <IconButton size="small" onClick={() => setSearchText('')}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
          sx={{ flex: 2, minWidth: 280 }}
        />
        <Button variant="outlined" onClick={acionarBusca}>Buscar</Button>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filtro-cliente-ativo">Status</InputLabel>
          <Select
            labelId="filtro-cliente-ativo"
            value={ativoFiltro}
            label="Status"
            onChange={(e) => { setPagina(1); setAtivoFiltro(e.target.value); }}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            <MenuItem value="true">Ativos</MenuItem>
            <MenuItem value="false">Inativos</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Tabela */}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox indeterminate={indeterminate} checked={todosSelecionados} onChange={selecionarTodos} />
              </TableCell>
              <SortableTh id="razaoSocial" label="Razão Social" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="nomeFantasia" label="Fantasia" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cnpj" label="CPF/CNPJ" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="cidadeUf" label="Cidade/UF" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="telefone" label="Telefone" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="email" label="Email" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="status" label="Status" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carregando ? (
              <TableRow>
                <TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            ) : clientesPagina.length > 0 ? (
              clientesOrdenados.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  sx={{ cursor: 'pointer', ...sxLinhaInativa(c.ativo !== false) }}
                  onClick={() => abrirForm(c.id)}
                >
                  <TableCell padding="checkbox" data-label="Selecionar" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selecionados.includes(c.id)}
                      onChange={() => selecionar(c.id)}
                    />
                  </TableCell>
                  <TableCell data-label="Razão social">{c.razaoSocial || '-'}</TableCell>
                  <TableCell data-label="Fantasia">{c.nomeFantasia || '-'}</TableCell>
                  <TableCell data-label="CPF/CNPJ">{fmtDoc(c.cnpj)}</TableCell>
                  <TableCell data-label="Cidade/UF">
                    {c.cidade || '-'}{c.estado ? `/${c.estado}` : ''}
                  </TableCell>
                  <TableCell data-label="Telefone">{c.telefone || '-'}</TableCell>
                  <TableCell data-label="Email">{c.email || '-'}</TableCell>
                  <TableCell data-label="Status">
                    <StatusAtivoChip ativo={c.ativo !== false} />
                  </TableCell>
                  <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Editar">
                      <IconButton color="primary" onClick={() => abrirForm(c.id)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton color="error" onClick={() => abrirDialogo(c.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum cliente encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveTable>

      {/* Paginação */}
      <Stack spacing={1} sx={{ mt: 2, alignItems: 'center' }}>
        <Pagination count={totalPaginas} page={pagina} onChange={mudarPagina} color="primary" />
        <Typography variant="body2" color="text.secondary">
          Exibindo {clientesPagina.length} de {totalItens} clientes (página {pagina}/{totalPaginas})
        </Typography>
      </Stack>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar cliente' : 'Novo cliente'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <FormClient
          key={form.id || 'novo'}
          embedded
          clientId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      {/* Diálogo de confirmação */}
      <Dialog open={dialogoAberto} onClose={fecharDialogo}>
        <DialogTitle>{idsParaExcluir.length > 1 ? 'Excluir Clientes' : 'Excluir Cliente'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {idsParaExcluir.length > 1
              ? `Tem certeza que deseja excluir os ${idsParaExcluir.length} clientes selecionados? Esta ação não pode ser desfeita.`
              : 'Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialogo} color="inherit">Cancelar</Button>
          <Button onClick={excluir} color="error" autoFocus>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarAberto}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={fecharSnackbar} severity={severidadeAlerta} sx={{ width: '100%' }}>
          {mensagemSnackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClientList;
